import { useMediaQuery } from "@/ui/hooks/useMediaQuery";
import {
    mobileRightDrawerOpenAtom,
    rightDrawerOpenAtom,
} from "@/features/Navigation/state";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useMetrics } from "./useMetrics";
import { DetailTableRow } from "../components/DetailTable";
import { useFlags } from "@/utils/flagsmith/useFlags";
import {
    FinanceLabField,
    getFinanceLabMetrics,
    ResampleTransform,
    useFinanceLabMetrics,
} from "@/entities/FinanceLab";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { useColorizedValue, DataSource } from "./useColorizedValue";
import { AccountLabel, PeriodKind } from "@/entities/Labeling/Account.model";
import {
    dataSourceOverridesAtom,
    MetricKey,
    selectedMetricForDrawerAtom,
} from "../state";
import { getLastValue } from "@/utils/metrics/metricsUtils";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";

export function useFinancialHighlightsData() {
    const isMobile = useMediaQuery("(max-width: 640px)");
    const [, setMobileRightDrawerOpen] = useAtom(mobileRightDrawerOpenAtom);
    const [, setRightDrawerOpen] = useAtom(rightDrawerOpenAtom);
    const setSelectedMetricForDrawer = useSetAtom(selectedMetricForDrawerAtom);
    const flags = useFlags();
    const useFinanceLabDefault = flags.enabled("use-finance-lab-metrics");
    const showPreAlphaFields = flags.enabled("show-pre-alpha-fields");
    const showComingSoonFields = flags.enabled("show-coming-soon-placeholders");
    const overrides = useAtomValue(dataSourceOverridesAtom);

    const shouldUseFinanceLab = (metricKey: MetricKey): boolean => {
        if (overrides[metricKey]) {
            return overrides[metricKey] === DataSource.FINANCE_LAB;
        }
        return useFinanceLabDefault;
    };

    const {
        revenue: metricsRevenue,
        ebitda: metricsEbitda,
        currentAssetsCashAndCashEquivalents,
        anualNetIncome,
        assets,
        liabilities,
    } = useMetrics();

    const {
        revenue: financeLabRevenue,
        ebitda: financeLabEbitda,
        cash: financeLabCash,
        totalAssets: financeLabTotalAssets,
        totalLiabilities: financeLabTotalLiabilities,
        netIncome: financeLabNetIncome,
        metrics: financeLabMetricsData,
        salesAndMarketingEfficiency,
        isLoading: isLoadingFinanceLabMetrics,
    } = useFinanceLabMetrics();

    const useFinanceLabRevenue = shouldUseFinanceLab(MetricKey.REVENUE);
    const useFinanceLabEbitda = shouldUseFinanceLab(MetricKey.EBITDA);
    const useFinanceLabCash = shouldUseFinanceLab(MetricKey.CASH);
    const useFinanceLabTotalAssets = shouldUseFinanceLab(
        MetricKey.TOTAL_ASSETS,
    );
    const useFinanceLabTotalLiabilities = shouldUseFinanceLab(
        MetricKey.TOTAL_LIABILITIES,
    );
    const useFinanceLabNetIncome = shouldUseFinanceLab(MetricKey.NET_INCOME);

    const revenue = useFinanceLabRevenue ? financeLabRevenue : metricsRevenue;
    const ebitda = useFinanceLabEbitda ? financeLabEbitda : metricsEbitda;
    const cash = useFinanceLabCash
        ? financeLabCash
        : currentAssetsCashAndCashEquivalents;
    const totalAssets = useFinanceLabTotalAssets
        ? financeLabTotalAssets
        : assets;
    const totalLiabilities = useFinanceLabTotalLiabilities
        ? financeLabTotalLiabilities
        : liabilities;
    const netIncome = useFinanceLabNetIncome
        ? financeLabNetIncome
        : anualNetIncome;

    const { data: metrics, isLoading: isLoadingMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: [
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_JUNIOR_DEBT_BALANCE_AVG,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_EQUITY_RAISED,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SENIOR_DEBT_BALANCE_AVG,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY,
                    FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS,
                    FinanceLabField.DEAL_DEBT_TO_EQUITY,
                    FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA,
                    FinanceLabField.DEAL_INTEREST_COVERAGE,
                    FinanceLabField.DEAL_DSCR,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
                ],
            },
        },
    );
    const { data: l3M1omfupMetrics, isLoading: isLoadingL3M1omfupMetrics } =
        useApiQuery(getFinanceLabMetrics, {
            params: {
                labels: [
                    FinanceLabField.DEAL_INTEREST_COVERAGE,
                    FinanceLabField.DEAL_DSCR,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
                ],
                resampleTransform:
                    ResampleTransform.LAST_3_MONTHS_1_ORDER_OF_MAGNITUDE_FILTER_UNCLOSED_PERIODS,
            },
        });
    const { data: cashBurnMetrics, isLoading: isLoadingCashBurnMetrics } =
        useApiQuery(getFinanceLabMetrics, {
            params: {
                labels: [
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY,
                ],
                periodKind: PeriodKind.MONTHLY,
            },
        });

    const { data: projectedMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY,
            ],
            periodKind: PeriodKind.YEARLY,
            resampleTransform: ResampleTransform.YEARLY,
        },
    });

    const totalEquityRaised = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_EQUITY_RAISED,
    )?.values;

    const totalAssetsToEquity = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY,
    )?.values;

    const cashBurnMonthly = cashBurnMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY,
    )?.values;

    const netIncomeMarginP = l3M1omfupMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
    )?.values;

    const grossMarginP = l3M1omfupMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
    )?.values;

    const ebitdaMarginP = l3M1omfupMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
    )?.values;

    const interestCoverage = l3M1omfupMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_INTEREST_COVERAGE,
    )?.values;

    const dscr = l3M1omfupMetrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_DSCR,
    )?.values;

    const grossDebtToAssets = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS,
    )?.values;

    const debtToEquity = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_DEBT_TO_EQUITY,
    )?.values;

    const grossDebtToEbitda = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA,
    )?.values;

    const handleMetricClick = (
        financeLabField: FinanceLabField,
        title: string,
    ) => {
        // Combine metrics from both sources to support all fields
        const allMetrics = [
            ...(metrics ?? []),
            ...(financeLabMetricsData ?? []),
            ...(cashBurnMetrics ?? []),
            ...(projectedMetrics ?? []),
        ];

        const metric = allMetrics.find(
            (m) => m.info.derived.label === financeLabField,
        );

        setSelectedMetricForDrawer({
            metric,
            title,
        });

        if (isMobile) {
            setMobileRightDrawerOpen(true);
        } else {
            setRightDrawerOpen(true);
        }
    };

    const handleRevenueClick = () =>
        handleMetricClick(
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
            "Revenue LTM",
        );

    const handleLineageTableClick = () => {
        setSelectedMetricForDrawer(null);
        if (isMobile) {
            setMobileRightDrawerOpen(true);
        } else {
            setRightDrawerOpen(true);
        }
    };

    const revenueColorized = useColorizedValue(
        revenue,
        useFinanceLabRevenue ? DataSource.FINANCE_LAB : DataSource.FINANCE_API,
        useFinanceLabRevenue
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE
            : AccountLabel.REVENUES,
    );
    const ebitdaColorized = useColorizedValue(
        ebitda,
        useFinanceLabEbitda ? DataSource.FINANCE_LAB : DataSource.FINANCE_API,
        useFinanceLabEbitda
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA
            : AccountLabel.EBITDA,
    );
    const grossMarginColorized = useColorizedValue(
        getLastValue(grossMarginP)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
    );
    const ebitdaMarginColorized = useColorizedValue(
        getLastValue(ebitdaMarginP)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
    );
    const cashBalanceColorized = useColorizedValue(
        cash,
        useFinanceLabCash ? DataSource.FINANCE_LAB : DataSource.FINANCE_API,
        useFinanceLabCash
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH
            : AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
    );
    const salesMarketingEfficiencyColorized = useColorizedValue(
        salesAndMarketingEfficiency,
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
    );

    const assetsColorized = useColorizedValue(
        totalAssets,
        useFinanceLabTotalAssets
            ? DataSource.FINANCE_LAB
            : DataSource.FINANCE_API,
        useFinanceLabTotalAssets
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS
            : AccountLabel.ASSETS,
    );

    const liabilitiesColorized = useColorizedValue(
        totalLiabilities,
        useFinanceLabTotalLiabilities
            ? DataSource.FINANCE_LAB
            : DataSource.FINANCE_API,
        useFinanceLabTotalLiabilities
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES
            : AccountLabel.LIABILITIES,
    );

    const netIncomeColorized = useColorizedValue(
        netIncome,
        useFinanceLabNetIncome
            ? DataSource.FINANCE_LAB
            : DataSource.FINANCE_API,
        useFinanceLabNetIncome
            ? FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME
            : AccountLabel.NET_INCOME,
    );
    const netIncomeMarginColorized = useColorizedValue(
        getLastValue(netIncomeMarginP)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
    );
    const totalEquityColorized = useColorizedValue(
        getLastValue(totalEquityRaised)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_EQUITY_RAISED,
    );
    const interestCoverageColorized = useColorizedValue(
        getLastValue(interestCoverage)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_INTEREST_COVERAGE,
    );

    const dscrColorized = useColorizedValue(
        getLastValue(dscr)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_DSCR,
    );
    const debtToAssetsColorized = useColorizedValue(
        getLastValue(grossDebtToAssets)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS,
    );
    const debtToEquityColorized = useColorizedValue(
        getLastValue(debtToEquity)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_DEBT_TO_EQUITY,
    );
    const debtToEbitdaColorized = useColorizedValue(
        getLastValue(grossDebtToEbitda)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA,
    );
    const assetToEquityColorized = useColorizedValue(
        getLastValue(totalAssetsToEquity)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY,
    );
    const monthlyCashBurnColorized = useColorizedValue(
        getLastValue(cashBurnMonthly)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY,
    );

    const generalData: DetailTableRow[] = [
        {
            label: "Revenue LTM",
            value: revenueColorized.value,
            valueClassName: revenueColorized.className,
            source: revenueColorized.source,
            sourceClassName: revenueColorized.sourceClassName,
            onClick: useFinanceLabRevenue
                ? handleRevenueClick
                : handleLineageTableClick,
        },
        {
            label: "EBITDA LTM",
            value: ebitdaColorized.value,
            valueClassName: ebitdaColorized.className,
            source: ebitdaColorized.source,
            sourceClassName: ebitdaColorized.sourceClassName,
            onClick: useFinanceLabEbitda
                ? () =>
                      handleMetricClick(
                          FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
                          "EBITDA LTM",
                      )
                : undefined,
        },
        {
            label: "Gross Margin %",
            value: grossMarginColorized.value,
            valueClassName: grossMarginColorized.className,
            source: grossMarginColorized.source,
            sourceClassName: grossMarginColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
                    "Gross Margin %",
                ),
        },
        {
            label: "EBITDA Margin %",
            value: ebitdaMarginColorized.value,
            valueClassName: ebitdaMarginColorized.className,
            source: ebitdaMarginColorized.source,
            sourceClassName: ebitdaMarginColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
                    "EBITDA Margin %",
                ),
        },
        {
            label: "Cash Balance",
            value: cashBalanceColorized.value,
            valueClassName: cashBalanceColorized.className,
            source: cashBalanceColorized.source,
            sourceClassName: cashBalanceColorized.sourceClassName,
            onClick: useFinanceLabCash
                ? () =>
                      handleMetricClick(
                          FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
                          "Cash Balance",
                      )
                : undefined,
        },
        {
            label: "Sales & Marketing Efficiency LTM",
            value: salesMarketingEfficiencyColorized.value,
            valueClassName: salesMarketingEfficiencyColorized.className,
            source: salesMarketingEfficiencyColorized.source,
            sourceClassName: salesMarketingEfficiencyColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
                    "Sales & Marketing Efficiency LTM",
                ),
        },
    ];

    const balanceSheetData: DetailTableRow[] = [
        {
            label: "Total Assets",
            value: assetsColorized.value,
            valueClassName: assetsColorized.className,
            source: assetsColorized.source,
            sourceClassName: assetsColorized.sourceClassName,
            onClick: useFinanceLabTotalAssets
                ? () =>
                      handleMetricClick(
                          FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
                          "Total Assets",
                      )
                : undefined,
        },
        {
            label: "Total Liabilities",
            value: liabilitiesColorized.value,
            valueClassName: liabilitiesColorized.className,
            source: liabilitiesColorized.source,
            sourceClassName: liabilitiesColorized.sourceClassName,
            onClick: useFinanceLabTotalLiabilities
                ? () =>
                      handleMetricClick(
                          FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
                          "Total Liabilities",
                      )
                : undefined,
        },
    ];

    const incomeStatementData: DetailTableRow[] = [
        {
            label: "Net Income",
            value: netIncomeColorized.value,
            valueClassName: netIncomeColorized.className,
            source: netIncomeColorized.source,
            sourceClassName: netIncomeColorized.sourceClassName,
            onClick: useFinanceLabNetIncome
                ? () =>
                      handleMetricClick(
                          FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
                          "Net Income",
                      )
                : undefined,
        },
        {
            label: "Net Income Margin %",
            value: netIncomeMarginColorized.value,
            valueClassName: netIncomeMarginColorized.className,
            source: netIncomeMarginColorized.source,
            sourceClassName: netIncomeMarginColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
                    "Net Income Margin %",
                ),
        },
    ];

    const capitalStructureData: DetailTableRow[] = [
        ...(showComingSoonFields
            ? [
                  {
                      label: "Senior Debt",
                      value: COMING_SOON,
                  },
              ]
            : []),
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Total Equity Raised",
                      value: totalEquityColorized.value,
                      valueClassName: totalEquityColorized.className,
                      source: totalEquityColorized.source,
                      sourceClassName: totalEquityColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_EQUITY_RAISED,
                              "Total Equity Raised",
                          ),
                  },
              ]
            : []),
        ...(showComingSoonFields
            ? [
                  {
                      label: "Junior Debt",
                      value: COMING_SOON,
                  },
              ]
            : []),
    ];

    const metricsProjectionsData: DetailTableRow[] = [
        {
            label: "Interest Coverage Ratio",
            value: interestCoverageColorized.value,
            valueClassName: interestCoverageColorized.className,
            source: interestCoverageColorized.source,
            sourceClassName: interestCoverageColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_INTEREST_COVERAGE,
                    "Interest Coverage Ratio",
                ),
        },
        {
            label: "Debt Service Coverage Ratio",
            value: dscrColorized.value,
            valueClassName: dscrColorized.className,
            source: dscrColorized.source,
            sourceClassName: dscrColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_DSCR,
                    "Debt Service Coverage Ratio",
                ),
        },
        {
            label: "Debt-to-Assets Ratio",
            value: debtToAssetsColorized.value,
            valueClassName: debtToAssetsColorized.className,
            source: debtToAssetsColorized.source,
            sourceClassName: debtToAssetsColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS,
                    "Debt-to-Assets Ratio",
                ),
        },
        {
            label: "Debt-to-Equity Ratio",
            value: debtToEquityColorized.value,
            valueClassName: debtToEquityColorized.className,
            source: debtToEquityColorized.source,
            sourceClassName: debtToEquityColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_DEBT_TO_EQUITY,
                    "Debt-to-Equity Ratio",
                ),
        },
        {
            label: "Debt-to-EBITDA Ratio",
            value: debtToEbitdaColorized.value,
            valueClassName: debtToEbitdaColorized.className,
            source: debtToEbitdaColorized.source,
            sourceClassName: debtToEbitdaColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA,
                    "Debt-to-EBITDA Ratio",
                ),
        },
        {
            label: "Asset-to-Equity",
            value: assetToEquityColorized.value,
            valueClassName: assetToEquityColorized.className,
            source: assetToEquityColorized.source,
            sourceClassName: assetToEquityColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY,
                    "Asset-to-Equity",
                ),
        },
        ...(showComingSoonFields
            ? [
                  {
                      label: "Fixed Charge Ratio",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Monthly cash burn",
            value: monthlyCashBurnColorized.value,
            valueClassName: monthlyCashBurnColorized.className,
            source: monthlyCashBurnColorized.source,
            sourceClassName: monthlyCashBurnColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY,
                    "Monthly cash burn",
                ),
        },
    ];

    // Process projected financials data for next 3 years table
    const getYearValue = (
        metricValues: { period: string; formattedValue?: string }[] | undefined,
        year: string,
    ): string => {
        if (!metricValues) return "-";
        const yearData = metricValues.find((item) => item.period === year);
        return yearData?.formattedValue || "-";
    };

    const projectedRevenueValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE,
    )?.values;

    const projectedGrossMarginValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P,
    )?.values;

    const projectedEbitdaValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA,
    )?.values;

    const projectedEbitdaMarginValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P,
    )?.values;

    const projectedCashValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH,
    )?.values;

    const projectedSalesMarketingEfficiencyValues = projectedMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY,
    )?.values;

    const projectedFinancialsData = [
        {
            metric: "Revenue",
            "2026": getYearValue(projectedRevenueValues, "2026"),
            "2027": getYearValue(projectedRevenueValues, "2027"),
            "2028": getYearValue(projectedRevenueValues, "2028"),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE,
                    "Revenue",
                ),
        },
        {
            metric: "Gross Margin %",
            "2026": getYearValue(projectedGrossMarginValues, "2026"),
            "2027": getYearValue(projectedGrossMarginValues, "2027"),
            "2028": getYearValue(projectedGrossMarginValues, "2028"),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P,
                    "Gross Margin %",
                ),
        },
        {
            metric: "EBITDA",
            "2026": getYearValue(projectedEbitdaValues, "2026"),
            "2027": getYearValue(projectedEbitdaValues, "2027"),
            "2028": getYearValue(projectedEbitdaValues, "2028"),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA,
                    "EBITDA",
                ),
        },
        {
            metric: "EBITDA Margin %",
            "2026": getYearValue(projectedEbitdaMarginValues, "2026"),
            "2027": getYearValue(projectedEbitdaMarginValues, "2027"),
            "2028": getYearValue(projectedEbitdaMarginValues, "2028"),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P,
                    "EBITDA Margin %",
                ),
        },
        {
            metric: "Cash Balance",
            "2026": getYearValue(projectedCashValues, "2026"),
            "2027": getYearValue(projectedCashValues, "2027"),
            "2028": getYearValue(projectedCashValues, "2028"),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH,
                    "Cash Balance",
                ),
        },
        {
            metric: "Sales & Marketing Efficiency",
            "2026": getYearValue(
                projectedSalesMarketingEfficiencyValues,
                "2026",
            ),
            "2027": getYearValue(
                projectedSalesMarketingEfficiencyValues,
                "2027",
            ),
            "2028": getYearValue(
                projectedSalesMarketingEfficiencyValues,
                "2028",
            ),
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY,
                    "Sales & Marketing Efficiency",
                ),
        },
    ];

    return {
        generalData,
        balanceSheetData,
        incomeStatementData,
        capitalStructureData,
        metricsProjectionsData,
        projectedFinancialsData,
        isLoading:
            isLoadingMetrics ||
            isLoadingFinanceLabMetrics ||
            isLoadingCashBurnMetrics ||
            isLoadingL3M1omfupMetrics,
    };
}
